import { useState } from 'react'
import { PhoneMissed, Voicemail } from 'lucide-react'
import type { MissedCallRecord } from '../types'
import { AudioPlayer } from './AudioPlayer'

interface MissedCallsListProps {
  calls: MissedCallRecord[]
}

export function MissedCallsList({ calls }: MissedCallsListProps) {
  const [playingSessionId, setPlayingSessionId] = useState<string | null>(null)

  if (calls.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500 text-sm">
        No missed calls
      </div>
    )
  }

  const handlePlayVoicemail = (sessionId: string) => {
    setPlayingSessionId(sessionId)
  }

  return (
    <div className="space-y-2">
      {calls.map((call) => {
        // Resolved = called back by operator OR customer called back (second attempt)
        const isResolved = call.wasCalledBack || call.wasSecondAttempt

        // Color logic:
        // - Green: resolved (called back)
        // - Yellow: repeat caller (same phone called multiple times, not yet resolved)
        // - Red: first missed call (urgent)
        const bgColor = isResolved
          ? 'bg-green-50 border-green-100'
          : call.isRepeatCaller
            ? 'bg-yellow-50 border-yellow-100'
            : 'bg-red-50 border-red-100'

        const iconBgColor = isResolved
          ? 'bg-green-100'
          : call.isRepeatCaller
            ? 'bg-yellow-100'
            : 'bg-red-100'

        const iconTextColor = isResolved
          ? 'text-green-600'
          : call.isRepeatCaller
            ? 'text-yellow-600'
            : 'text-red-600'

        return (
          <div
            key={call.id}
            className={`flex items-center justify-between p-3 rounded-lg border ${bgColor}`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${iconBgColor}`}>
                <PhoneMissed className={`w-4 h-4 ${iconTextColor}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {call.fromName}
                </p>
                <p className="text-xs text-gray-500">
                  {call.fromPhone} • {call.date} {call.time}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {call.wasCalledBack ? (
                <div className="text-right">
                  <span className="text-xs text-green-600 font-medium">
                    Called back in {call.callbackMinutes} min
                  </span>
                  {call.callbackOperator && (
                    <p className="text-xs text-red-500 font-medium">
                      {call.callbackOperator}
                    </p>
                  )}
                </div>
              ) : call.wasSecondAttempt ? (
                <span className="text-xs text-green-600 font-medium">
                  Second attempt in {call.secondAttemptMinutes} min
                </span>
              ) : call.hasVoicemail ? (
                <button
                  type="button"
                  onClick={() => handlePlayVoicemail(call.telephonySessionId)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-full transition-colors"
                >
                  <Voicemail className="w-3.5 h-3.5" />
                  Voicemail
                </button>
              ) : (
                <span className="text-xs text-gray-400">No voicemail</span>
              )}
            </div>
          </div>
        )
      })}

      {/* Audio Player */}
      {playingSessionId && (
        <AudioPlayer
          sessionId={playingSessionId}
          csrName="Voicemail"
          onClose={() => setPlayingSessionId(null)}
        />
      )}
    </div>
  )
}
